import React, { useEffect, useRef } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import aboutData from "../../data/about.json";
import { Sparkles, Quote, Target, Eye, Calendar } from "lucide-react";

/* ─────────────────────────────────────────────
 MILESTONE NODE (timeline dot + content)
───────────────────────────────────────────── */
const MilestoneNode = ({ milestone, index, isLeft }) => {
  const nodeRef = useRef(null);
  const dotRef = useRef(null);
  const lineRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: nodeRef.current,
          start: "top 82%",
        },
      });

      /* Dot scale */
      tl.fromTo(
        dotRef.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.4, ease: "back.out(2)" },
      );

      /* Line draw */
      tl.fromTo(
        lineRef.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.5,
          ease: "power3.out",
          transformOrigin: isLeft ? "right center" : "left center",
        },
        "-=0.2",
      );

      /* Card reveal */
      tl.fromTo(
        cardRef.current,
        { x: isLeft ? 40 : -40, opacity: 0, scale: 0.95 },
        { x: 0, opacity: 1, scale: 1, duration: 0.6, ease: "power3.out" },
        "-=0.3",
      );
    }, nodeRef);

    return () => ctx.revert();
  }, [isLeft]);

  /* Hover ripple */
  const handleEnter = () => {
    gsap.to(dotRef.current, {
      scale: 1.4,
      duration: 0.3,
      ease: "power2.out",
    });
    gsap.to(cardRef.current, {
      y: -4,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  const handleLeave = () => {
    gsap.to(dotRef.current, {
      scale: 1,
      duration: 0.3,
      ease: "power2.out",
    });
    gsap.to(cardRef.current, {
      y: 0,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  return (
    <div
      ref={nodeRef}
      className={`relative flex items-center gap-6
 ${isLeft ? "lg:flex-row-reverse lg:text-right" : "lg:flex-row"}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* Timeline dot */}
      <div
        ref={dotRef}
        className="relative z-10 flex-shrink-0 w-5 h-5 rounded-full
 flex items-center justify-center cursor-pointer"
        style={{
          background: milestone.color,
          boxShadow: `0 0 20px ${milestone.color}60`,
          opacity: 0,
        }}
      >
        <div className="w-2 h-2 rounded-full bg-white" />
        {/* Ping animation */}
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: milestone.color, opacity: 0.3 }}
        />
      </div>

      {/* Connector line (only visible on lg) */}
      <div
        ref={lineRef}
        className="hidden lg:block w-12 h-px flex-shrink-0"
        style={{
          background: `linear-gradient(${isLeft ? "270deg" : "90deg"}, ${milestone.color}60, transparent)`,
          transform: "scaleX(0)",
        }}
      />

      {/* Card */}
      <div
        ref={cardRef}
        className="relative flex-1 rounded-2xl p-5 cursor-default
 transition-shadow duration-300 hover:shadow-card-hover"
        style={{
          background: `${milestone.color}08`,
          border: `1px solid ${milestone.color}25`,
          backdropFilter: "blur(12px)",
          opacity: 0,
        }}
      >
        {/* Year badge */}
        <div className="flex items-center gap-2 mb-2">
          <Calendar size={11} style={{ color: milestone.color }} />
          <span
            className="text-[10px] font-black tracking-[0.2em] uppercase font-mono"
            style={{ color: milestone.color }}
          >
            {milestone.year}
          </span>
        </div>

        {/* Title */}
        <h4 className="font-bold text-gray-900 text-sm leading-snug mb-1.5">
          {milestone.title}
        </h4>

        {/* Description */}
        <p className="text-xs text-gray-600 leading-relaxed">
          {milestone.description}
        </p>

        {/* Corner accent */}
        <div
          className="absolute top-2 right-2 w-2 h-2 rounded-full"
          style={{ background: milestone.color, opacity: 0.4 }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 VERTICAL TIMELINE (mobile)
 ALTERNATING TIMELINE (desktop)
───────────────────────────────────────────── */
const Timeline = ({ milestones }) => {
  const trackRef = useRef(null);

  /* Animated progress line */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        trackRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: {
            trigger: trackRef.current,
            start: "top 80%",
            end: "bottom 60%",
            scrub: 1,
          },
        },
      );
    }, trackRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="relative">
      {/* Central line */}
      <div className="absolute left-[10px] lg:left-1/2 top-0 bottom-0 w-px lg:-translate-x-px">
        {/* Track background */}
        <div className="absolute inset-0 bg-gray-200" />
        {/* Animated fill */}
        <div
          ref={trackRef}
          className="absolute inset-0 origin-top"
          style={{
            background: "linear-gradient(to bottom, #3b82f6, #f59e0b, #6366f1)",
            transform: "scaleY(0)",
          }}
        />
      </div>

      {/* Milestone nodes */}
      <div className="space-y-10 lg:space-y-14 pl-10 lg:pl-0">
        {milestones.map((m, i) => (
          <div
            key={i}
            className={`lg:w-1/2 ${
              i % 2 === 0 ? "lg:ml-auto lg:pl-14" : "lg:mr-auto lg:pr-14"
            }`}
          >
            <MilestoneNode milestone={m} index={i} isLeft={i % 2 !== 0} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 MISSION / VISION CARD
───────────────────────────────────────────── */
const MissionVisionCard = ({ data, icon: Icon, color, delay = 0 }) => {
  const cardRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 85%",
          },
        },
      );

      gsap.to(glowRef.current, {
        opacity: 0.7,
        scale: 1.1,
        duration: 3,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay,
      });
    }, cardRef);

    return () => ctx.revert();
  }, [delay]);

  const handleEnter = () => {
    gsap.to(cardRef.current, {
      y: -6,
      scale: 1.02,
      duration: 0.35,
      ease: "power2.out",
    });
  };

  const handleLeave = () => {
    gsap.to(cardRef.current, {
      y: 0,
      scale: 1,
      duration: 0.35,
      ease: "power2.out",
    });
  };

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative rounded-3xl p-7 overflow-hidden cursor-default
 transition-shadow duration-300 hover:shadow-card-hover"
      style={{
        background: `${color}08`,
        border: `1px solid ${color}25`,
        backdropFilter: "blur(16px)",
        opacity: 0,
      }}
    >
      {/* Glow */}
      <div
        ref={glowRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 60% 60% at 50% 0%, ${color}15 0%, transparent 60%)`,
          opacity: 0.5,
        }}
      />

      {/* Icon */}
      <div className="relative z-10 space-y-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: `${color}20`, border: `1px solid ${color}30` }}
        >
          <Icon size={24} style={{ color }} />
        </div>

        <h3 className="font-display font-bold text-gray-900 text-xl">
          {data.title}
        </h3>

        <div
          className="h-px w-12 rounded-full"
          style={{
            background: `linear-gradient(90deg, ${color}60, transparent)`,
          }}
        />

        <p className="text-gray-600 text-sm leading-relaxed">{data.text}</p>
      </div>

      {/* Watermark */}
      <div
        className="absolute -bottom-4 -right-4 font-black font-display
 leading-none pointer-events-none select-none"
        style={{
          fontSize: "6rem",
          color,
          opacity: 0.04,
        }}
      >
        {data.title === "Our Mission" ? "M" : "V"}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 IMAGE PANEL with parallax
───────────────────────────────────────────── */
const StoryImage = ({ src }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Image parallax */
      gsap.to(imgRef.current, {
        yPercent: -15,
        ease: "none",
        scrollTrigger: {
          trigger: wrapRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });

      /* Clip-path reveal */
      gsap.fromTo(
        wrapRef.current,
        { clipPath: "inset(0 100% 0 0)" },
        {
          clipPath: "inset(0 0% 0 0)",
          duration: 1.2,
          ease: "power4.inOut",
          scrollTrigger: {
            trigger: wrapRef.current,
            start: "top 75%",
          },
        },
      );

      /* Frame draw */
      gsap.fromTo(
        frameRef.current,
        { scaleX: 0, scaleY: 0 },
        {
          scaleX: 1,
          scaleY: 1,
          duration: 0.8,
          delay: 0.5,
          ease: "power3.out",
          transformOrigin: "top left",
          scrollTrigger: {
            trigger: wrapRef.current,
            start: "top 75%",
          },
        },
      );
    }, wrapRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="relative">
      {/* Frame */}
      <div
        ref={frameRef}
        className="absolute -top-4 -left-4 w-24 h-24
 border-l-2 border-t-2 border-secondary-400/50 rounded-tl-3xl
 pointer-events-none z-10"
        style={{ transform: "scaleX(0) scaleY(0)" }}
      />
      <div
        className="absolute -bottom-4 -right-4 w-24 h-24
 border-r-2 border-b-2 border-primary-500/50 rounded-br-3xl
 pointer-events-none z-10"
      />

      <div
        ref={wrapRef}
        className="relative rounded-3xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: "4/5", clipPath: "inset(0 100% 0 0)" }}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Agora Consulting Story"
          className="w-full h-[120%] object-cover -mt-[10%]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(6,13,36,0.7) 0%, rgba(6,13,36,0.1) 50%, transparent)",
          }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN OUR STORY SECTION
───────────────────────────────────────────── */
const OurStory = () => {
  const { story } = aboutData;

  const sectionRef = useRef(null);
  const labelRef = useRef(null);
  const headRef = useRef(null);
  const lineRef = useRef(null);
  const introRef = useRef(null);
  const parasRef = useRef([]);
  const bgRef = useRef(null);

  /* Header animation */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 75%",
        },
      });

      tl.fromTo(
        labelRef.current,
        { x: -25, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
      )
        .fromTo(
          headRef.current?.children ?? [],
          { y: 50, opacity: 0, skewY: 2 },
          {
            y: 0,
            opacity: 1,
            skewY: 0,
            stagger: 0.12,
            duration: 0.8,
            ease: "power4.out",
          },
          "-=0.2",
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.6,
            ease: "power3.out",
            transformOrigin: "left",
          },
          "-=0.4",
        )
        .fromTo(
          introRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.3",
        );
    });

    return () => ctx.revert();
  }, []);

  /* Paragraphs stagger */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        parasRef.current,
        { y: 25, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.15,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: parasRef.current[0],
            start: "top 80%",
          },
        },
      );
    });

    return () => ctx.revert();
  }, []);

  /* BG parallax */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(bgRef.current, {
        yPercent: 25,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="our-story"
      className="relative overflow-hidden section-py"
      style={{
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)",
      }}
    >
      {/* ── BG ── */}
      <div ref={bgRef} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(29,78,216,0.1) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute bottom-1/4 left-0 w-80 h-80 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(245,158,11,0.07) 0%, transparent 70%)",
            filter: "blur(50px)",
          }}
        />
      </div>

      {/* Edge fades */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{
          background: "linear-gradient(to bottom, #ffffff, transparent)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{ background: "linear-gradient(to top, #ffffff, transparent)" }}
      />

      {/* Left vertical label */}
      <div
        className="absolute left-4 top-1/2 -translate-y-1/2 hidden xl:flex
 flex-col items-center gap-3 pointer-events-none z-10"
      >
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(29,78,216,0.4))",
          }}
        />
        <p
          className="text-[10px] font-semibold tracking-[0.4em] text-gray-400 uppercase"
          style={{ writingMode: "vertical-lr" }}
        >
          Our Story
        </p>
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to top, transparent, rgba(29,78,216,0.4))",
          }}
        />
      </div>

      <div className="container-fluid relative z-20">
        {/* ── INTRO SECTION ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-center mb-5 lg:mb-28">
          {/* Left: Image */}
          <StoryImage src={story.image} />

          {/* Right: Text */}
          <div className="space-y-6">
            <div ref={headRef} className="overflow-hidden space-y-1">
              <h2
                className="font-display font-black text-gray-900 leading-tight"
                style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
              >
                {story.headline}
              </h2>
              <h2
                className="font-display font-black text-gradient-primary leading-tight"
                style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
              >
                {story.headlineAccent}
              </h2>
            </div>

            <div
              ref={lineRef}
              className="h-[2px] w-20 rounded-full"
              style={{
                background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
                transform: "scaleX(0)",
                transformOrigin: "left",
              }}
            />

            <p
              ref={introRef}
              className="text-primary-600 leading-relaxed text-[15px] font-medium italic"
              style={{ opacity: 0 }}
            >
              {story.intro}
            </p>

            <div className="space-y-4">
              {story.paragraphs.map((para, i) => (
                <p
                  key={i}
                  ref={(el) => (parasRef.current[i] = el)}
                  className="text-gray-600 leading-relaxed text-[15px]"
                  style={{ opacity: 0 }}
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* ── MISSION / VISION ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20 lg:mb-28">
          <MissionVisionCard
            data={story.mission}
            icon={Target}
            color="#3b82f6"
            delay={0}
          />
          <MissionVisionCard
            data={story.vision}
            icon={Eye}
            color="#f59e0b"
            delay={0.15}
          />
        </div>

        {/* ── TIMELINE ── */}
        <div className="max-w-4xl mx-auto">
          {/* Timeline header */}
          <div className="text-center mb-5">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-gray-500 mb-3">
              Our Journey
            </p>
            <div
              className="h-px w-16 mx-auto rounded-full"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(29,78,216,0.4), transparent)",
              }}
            />
          </div>

          <Timeline milestones={story.milestones} />
        </div>

        {/* ── BOTTOM QUOTE ── */}
        <div className="mt-20 lg:mt-28 text-center max-w-2xl mx-auto">
          <Quote size={28} className="text-secondary-400/40 mx-auto mb-4" />
          <p className="font-display font-medium text-gray-900 leading-relaxed italic text-lg">
            "The name'Agora' draws from the ancient Greek marketplace of ideas —
            a space where knowledge, dialogue, and action converge."
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="h-px w-8 bg-secondary-400/50" />
            <span className="text-secondary-400 text-sm font-semibold tracking-wider">
              Agora Consulting
            </span>
            <div className="h-px w-8 bg-secondary-400/50" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStory;
